// Pure helpers for the Chart of Accounts feature.

import type React from "react";
import type { AccountType, TransactionMethod } from "./types";
import { PALETTE } from "./constants";

export function fmtCurrency(n: number): string {
  const sign = n < 0 ? "-" : "+";
  return sign + "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtBal(n: number, decimals = 0): string {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Builds a CSV string of the current accounts, for the Export icon in the toolbar. */
export function accountsToCsv(accounts: Array<{ number: string; name: string; type: string; detailType?: string; balance: number }>): string {
  const header = ["Number", "Name", "Type", "Detail type", "Balance"];
  const rows = accounts.map((a) => [a.number, a.name, a.type, a.detailType ?? "", a.balance.toFixed(2)]);
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [header, ...rows].map((r) => r.map((c) => escape(String(c))).join(",")).join("\n");
}

/** Triggers a browser download of the given string as a .csv file. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Legacy pill helpers for DrillView, kept so existing pages don't break.
export function typePillStyle(type: AccountType): React.CSSProperties {
  if (type === "Asset" || type === "Income") return { background: PALETTE.tealWash, color: PALETTE.tealDark };
  if (type === "Expense") return { background: PALETTE.velaWash, color: PALETTE.velaDeep };
  if (type === "Liability") return { background: PALETTE.redWash, color: PALETTE.redInk };
  return { background: PALETTE.amberWash, color: PALETTE.amberInk };
}

export function methodPillStyle(method: TransactionMethod | string): React.CSSProperties {
  const m = String(method).toLowerCase();
  if (m.includes("transfer") || m.includes("deposit")) return { background: PALETTE.tealWash, color: PALETTE.tealDark };
  if (m.includes("invoice") || m.includes("insurance") || m.includes("government")) return { background: PALETTE.velaWash, color: PALETTE.velaDeep };
  if (m.includes("cheque") || m.includes("cash")) return { background: PALETTE.amberWash, color: PALETTE.amberInk };
  return { background: PALETTE.panel, color: PALETTE.ink2 };
}